import { Link } from 'react-router-dom'
import type { Thesis, ThesisAllocation, ThesisHealthScore } from '../../types/thesis'
import { STATUS_COLORS, DRIFT_STATUS_COLORS, DRIFT_STATUS_LABELS, HEALTH_STATUS_COLORS, HEALTH_SCORE_COLORS } from '../../types/thesis'

interface ThesisProgressRowProps {
  thesis: Thesis
  allocation?: ThesisAllocation
  healthScore?: ThesisHealthScore
}

export function ThesisProgressRow({ thesis, allocation, healthScore }: ThesisProgressRowProps) {
  const hasTarget = thesis.target_allocation_pct !== null && thesis.target_allocation_pct !== undefined
  const hasAllocation = allocation !== undefined

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Calculate progress bar values
  const currentPct = allocation?.current_allocation_pct ?? 0
  const targetPct = thesis.target_allocation_pct ?? 0
  const displayCurrentPct = Math.min(currentPct, 100)

  // Determine if under-allocated (eligible for deploy action)
  const isUnderAllocated = allocation && allocation.drift_pct < -2

  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      {/* Row 1: Status, Name, Health Score */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[thesis.status]}`}
          >
            {thesis.status}
          </span>
          <span className="font-medium text-gray-900">{thesis.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Health Score */}
          {healthScore && (
            <div className="flex items-center gap-1.5">
              <span className={`text-sm font-bold ${HEALTH_SCORE_COLORS[healthScore.status]}`}>
                {healthScore.health_score.toFixed(0)}
              </span>
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${HEALTH_STATUS_COLORS[healthScore.status]}`}>
                {healthScore.status}
              </span>
            </div>
          )}
          {/* Drift Status */}
          {allocation && (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full ${DRIFT_STATUS_COLORS[allocation.drift_status]}`}
            >
              {DRIFT_STATUS_LABELS[allocation.drift_status]}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Progress Bar */}
      {hasTarget && (
        <div className="mb-2">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">
              {hasAllocation ? `${currentPct.toFixed(1)}%` : '0%'} / {targetPct.toFixed(1)}%
            </span>
            {allocation && allocation.drift_pct !== 0 && (
              <span
                className={`font-medium ${
                  allocation.drift_pct > 0 ? 'text-orange-600' : 'text-blue-600'
                }`}
              >
                {allocation.drift_pct > 0 ? '+' : ''}
                {allocation.drift_pct.toFixed(1)}%{' '}
                {allocation.drift_pct > 0 ? 'over' : 'under'}
              </span>
            )}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden relative">
            {/* Target indicator line */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
              style={{ left: `${Math.min(targetPct, 100)}%` }}
            />
            {/* Current allocation bar */}
            <div
              className={`h-full transition-all duration-300 ${
                !hasAllocation
                  ? 'bg-gray-300'
                  : allocation.drift_status === 'ok'
                    ? 'bg-green-500'
                    : allocation.drift_status === 'warning'
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
              }`}
              style={{ width: `${displayCurrentPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Row 3: Metadata + Actions */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4 text-gray-500">
          <span>{thesis.rules_count ?? 0} rules</span>
          {allocation && (
            <>
              <span>{allocation.holdings_count} holdings</span>
              <span>{formatCurrency(allocation.total_market_value)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Deploy button for under-allocated theses */}
          {isUnderAllocated && (
            <Link
              to={`/strategy/deploy?thesis_id=${thesis.id}`}
              className="px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              Deploy
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
